import { ICON_PATHS, ICON_VIEWBOX, type IconName } from '@/features/common/icons.generated';
import '@/features/common/icon.css';

export type { IconName };

/**
 * A decorative icon, as markup rather than a CSS mask on an empty span.
 *
 * Three things about this component are load-bearing, not stylistic:
 *
 * - It renders an inline <svg> whose paths are `fill="currentColor"`, never an <img>. The icons
 *   here take their colour from the text beside them — a sidebar item's icon turns blue when the
 *   item is active, the dropdown chevron greys out with its disabled label, the logout icon is
 *   white because it sits on a blue button. An <img> cannot inherit colour and would strand a
 *   grey icon next to coloured text in every one of those states.
 * - It sets no width/height attribute. The size still comes from CSS, from the same class the
 *   old span carried, so the rendered box is unchanged. Omitting preserveAspectRatio leaves the
 *   default `xMidYMid meet`, which is exactly what `mask: ... no-repeat center / contain` did —
 *   the icons are mostly 25x24, not square, so this is what keeps them from shifting.
 * - aria-hidden is hardcoded, not a prop. Every icon in the cabinet sits beside a text label that
 *   already says the same thing; exposing them would just double every button's announcement.
 *   focusable="false" keeps legacy engines from making the <svg> a tab stop.
 */
export function Icon({ name, className }: { name: IconName; className?: string }) {
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      viewBox={ICON_VIEWBOX[name]}
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}
